import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { EntityKind, Identity, IdentityToken } from '@/types/identity';
import { identityToken } from '@/utils/identity';
import type { ActionMenuItem } from '@/components/EntityActionsMenu/EntityActionsMenu';
import { ScrollEndFade } from '@/components/ScrollEndFade';
import { IdentityAvatarGridCell } from './IdentityAvatarGridCell';
import { UndefinedIdentityCell } from './UndefinedIdentityCell';
import {
    IDENTITY_GRID_GAP_CLASSES,
    IDENTITY_GRID_MAX_HEIGHT_CLASSES,
    IDENTITY_GRID_LABELLED_COLUMN_CLASSES,
    IDENTITY_GRID_UNLABELLED_COLUMN_CLASSES,
    type IdentityAvatarSize,
} from './identityAvatarSize';

export interface IdentityGridPagination {
    initialRows: number;
    pageSize: number;
}

export interface IdentityGridScrollArea {
    maxRows: number;
    availableHeightClassName: string;
    fadeClassName: string;
}

export interface UndefinedIdentityCellConfig {
    token: IdentityToken;
    kind: EntityKind;
    label: string;
    description: string;
    count: number;
}

interface IdentityAvatarGridProps {
    identities: Identity[];
    selectedTokens: Set<IdentityToken>;
    onToggle: (token: IdentityToken) => void;
    buildMenuItems: (identity: Identity) => ActionMenuItem[];
    columns: number;
    showLabels: boolean;
    size: IdentityAvatarSize;
    undefinedCells?: UndefinedIdentityCellConfig[];
    pagination?: IdentityGridPagination;
    scrollArea?: IdentityGridScrollArea;
    emptyLabel: string;
}

export function IdentityAvatarGrid({
    identities,
    selectedTokens,
    onToggle,
    buildMenuItems,
    columns,
    showLabels,
    size,
    undefinedCells = [],
    pagination,
    scrollArea,
    emptyLabel,
}: IdentityAvatarGridProps) {
    const { t } = useTranslation();
    const initialVisibleCount = pagination
        ? Math.max(pagination.initialRows * columns - undefinedCells.length, 0)
        : identities.length;
    const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const shouldScrollToBottom = useRef(false);

    useEffect(() => {
        if (!shouldScrollToBottom.current || scrollContainer === null) {
            return;
        }
        shouldScrollToBottom.current = false;
        scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth',
        });
    }, [scrollContainer, visibleCount]);

    if (identities.length === 0 && undefinedCells.length === 0) {
        return (
            <div className="flex h-full items-center justify-center text-sm font-normal text-grey-300">
                {emptyLabel}
            </div>
        );
    }

    const columnClassesForSize = showLabels
        ? IDENTITY_GRID_LABELLED_COLUMN_CLASSES[size]
        : IDENTITY_GRID_UNLABELLED_COLUMN_CLASSES[size];
    const columnClasses =
        columnClassesForSize[columns] ?? columnClassesForSize[4];

    const visibleIdentities = pagination
        ? identities.slice(0, visibleCount)
        : identities;
    const remainingCount = identities.length - visibleIdentities.length;
    const renderedRows = Math.ceil(
        (visibleIdentities.length + undefinedCells.length) / columns,
    );
    const visibleRows = scrollArea
        ? Math.min(Math.max(renderedRows, 1), scrollArea.maxRows)
        : 0;

    const gridElement = (
        <div
            className={cn(
                'grid',
                IDENTITY_GRID_GAP_CLASSES[size],
                columnClasses,
            )}
        >
            {undefinedCells.map((undefinedCell) => (
                <UndefinedIdentityCell
                    key={undefinedCell.token}
                    token={undefinedCell.token}
                    kind={undefinedCell.kind}
                    label={undefinedCell.label}
                    description={undefinedCell.description}
                    count={undefinedCell.count}
                    isSelected={selectedTokens.has(undefinedCell.token)}
                    showLabel={showLabels}
                    size={size}
                    onToggle={() => onToggle(undefinedCell.token)}
                />
            ))}
            {visibleIdentities.map((identity) => {
                const token = identityToken(identity);
                return (
                    <IdentityAvatarGridCell
                        key={token}
                        identity={identity}
                        isSelected={selectedTokens.has(token)}
                        showLabel={showLabels}
                        size={size}
                        onToggle={() => onToggle(token)}
                        menuItems={buildMenuItems(identity)}
                    />
                );
            })}
        </div>
    );

    return (
        <div className="flex min-h-0 flex-col">
            {scrollArea ? (
                <div
                    className={cn(
                        'relative flex min-h-0 flex-col',
                        scrollArea.availableHeightClassName,
                    )}
                >
                    <div
                        ref={setScrollContainer}
                        className={cn(
                            '-mx-1.5 min-h-0 overflow-y-auto p-1.5 scrollbar-hover-thumb',
                            'transition-[max-height] duration-500 ease-out motion-reduce:transition-none',
                            IDENTITY_GRID_MAX_HEIGHT_CLASSES[size][visibleRows],
                        )}
                    >
                        {gridElement}
                    </div>
                    <ScrollEndFade
                        container={scrollContainer}
                        contentVersions={[visibleIdentities.length]}
                        className={scrollArea.fadeClassName}
                    />
                </div>
            ) : (
                gridElement
            )}
            {pagination && identities.length > initialVisibleCount ? (
                <div className="h-9 shrink-0">
                    {remainingCount > 0 && (
                        <button
                            type="button"
                            onClick={() => {
                                shouldScrollToBottom.current = true;
                                setVisibleCount(
                                    visibleCount + pagination.pageSize,
                                );
                            }}
                            data-testid="identity-grid-load-more"
                            className="mx-auto mt-4 block text-sm-medium text-grey-300 transition-colors hover:text-white-100"
                        >
                            {t('identityFilter.loadMore', {
                                count: Math.min(
                                    remainingCount,
                                    pagination.pageSize,
                                ),
                            })}
                        </button>
                    )}
                </div>
            ) : null}
        </div>
    );
}
